import React from "react";
import { Check, ArrowUpRight } from "lucide-react";
import { PageIntro } from "../components/PageIntro";
import { Reveal, StaggerGroup, StaggerItem } from "../components/Reveal";
import { PLANS, PLAN_PERKS, waLink } from "../lib/data";

export default function Plans() {
  return (
    <div data-testid="plans-page">
      <PageIntro
        label="Subscription plans"
        titleLines={["Simple weekly plans,", "sized for your", "household."]}
        subtitle="All plans are managed and paid securely through WhatsApp. Every territory has the same plans, guaranteed — pricing and exact contents are confirmed by your local unit."
      />

      {/* Plan cards */}
      <section className="px-5 md:px-10 py-12 md:py-16">
        <div className="max-w-[1400px] mx-auto">
          <StaggerGroup className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {PLANS.map((plan) => (
              <StaggerItem key={plan.name}>
                <div
                  data-testid={`plan-card-${plan.name.toLowerCase().replace(/\s/g, "-")}`}
                  className={`relative h-full rounded-[1.5rem] p-8 md:p-10 border transition-transform duration-500 hover:-translate-y-2 ${
                    plan.highlight
                      ? "bg-brand-forest text-brand-bg border-brand-forest"
                      : "bg-brand-card text-brand-ink border-black/5"
                  }`}
                >
                  {plan.highlight && (
                    <span className="absolute top-6 right-6 font-mono text-[10px] uppercase tracking-[0.2em] bg-brand-sprout text-white px-3 py-1 rounded-full">
                      {plan.tag}
                    </span>
                  )}
                  {!plan.highlight && (
                    <span className="font-mono text-xs uppercase tracking-[0.2em] text-brand-sprout">
                      {plan.tag}
                    </span>
                  )}
                  <h3 className={`font-serif text-4xl md:text-5xl mt-3 ${plan.highlight ? "text-brand-bg" : "text-brand-ink"}`}>
                    {plan.name}
                  </h3>
                  <p className={`mt-4 leading-relaxed ${plan.highlight ? "text-brand-bg/80" : "text-brand-muted"}`}>
                    {plan.description}
                  </p>
                  <ul className="mt-8 space-y-3">
                    {plan.features.map((f, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <Check size={18} className={plan.highlight ? "text-brand-sprout mt-0.5 shrink-0" : "text-brand-forest mt-0.5 shrink-0"} />
                        <span className={`text-sm ${plan.highlight ? "text-brand-bg/90" : "text-brand-muted"}`}>{f}</span>
                      </li>
                    ))}
                  </ul>
                  <a
                    href={waLink(`Hi 40X Leaves, I'd like the ${plan.name} plan. My pincode is `)}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`plan-cta-${plan.name.toLowerCase().replace(/\s/g, "-")}`}
                    className={`mt-10 inline-flex w-full items-center justify-center gap-2 rounded-full px-6 py-3.5 text-sm font-semibold transition-transform duration-300 hover:scale-[1.03] ${
                      plan.highlight
                        ? "bg-brand-bg text-brand-forest"
                        : "bg-brand-forest text-brand-bg hover:bg-brand-sprout"
                    }`}
                  >
                    Choose {plan.name} <ArrowUpRight size={16} />
                  </a>
                </div>
              </StaggerItem>
            ))}
          </StaggerGroup>
        </div>
      </section>

      {/* Perks */}
      <section className="px-5 md:px-10 pb-24 md:pb-32">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-black/10 rounded-[1.5rem] overflow-hidden border border-black/10">
              {PLAN_PERKS.map((perk, i) => (
                <div key={i} className="bg-brand-bg p-8">
                  <Check size={20} className="text-brand-sprout" />
                  <p className="mt-4 font-serif text-xl text-brand-ink leading-snug">{perk}</p>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
